import { Hotel } from 'ts/hotel/interfaces/HotelResponse';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { hotelsTripadvisor } from 'services/hotel';
import { SerpResponse } from 'ts/hotel/interfaces/SerpResponse';
import { useTranslation } from 'react-i18next';
import ButtonsLang from 'components/shared/Proposal/TripadvisorGoogle/ButtonsLang';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { MapHotelContext } from 'contexts/MapHotelContext';
import { useLoadGoogle } from 'hooks/useLoadGoogle';
import GoogleTabContent from './GoogleTabContent';
import TripadvisorTabContent from './TripadvisorTabContent';
import { getKey } from 'utils/hotel';
import { gIco, trIco } from 'constants/generic';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import Tabs from 'components/ui/Tabs/Tabs';

interface GoogleHotelInfoProps {
  hotel: Hotel;
}

const GoogleHotelInfo: FC<GoogleHotelInfoProps> = (props) => {
  const [res, setRes] = useState<SerpResponse>();
  const [loading, setLoading] = useState('');
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const { gh, setGh, setTa, ta } = useContext(MapHotelContext);
  const { loadGoogle } = useLoadGoogle();
  const serpData = gh?.find((data) => data.hotel.htuKey === props.hotel.htuKey)?.data;
  const taData = ta?.find((data) => data.hotel.htuKey === props.hotel.htuKey)?.data;
  const { hotelIndex } = useContext(HotelConfigContext);
  const searchToken = sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex));

  useEffect(() => {
    loadTripadvisor();
    if (!loading && !res && !serpData) {
      onClickLoad(localStorage.getItem('language') || 'en');
    } else if (serpData) {
      setRes(serpData);
      setLoading('');
    }
  }, []); //eslint-disable-line

  const onClickLoad = async (lang: string) => {
    setRes(undefined);
    setLoading(lang);

    const newRes = await loadGoogle(lang, props.hotel, searchToken || '');
    if (newRes) {
      const newSerpData = { data: newRes, hotel: props.hotel };
      setGh?.((prev) => [
        ...prev.filter((it) => it.hotel.htuKey !== newSerpData.hotel.htuKey),
        newSerpData,
      ]);
      setRes(newRes);
    }
    setLoading('');
  };

  const loadTripadvisor = async () => {
    if (taData) return;
    const lang = localStorage.getItem('language') || 'en';
    const newRes = await hotelsTripadvisor({
      bpid: props.hotel.item.bpid,
      lang,
    });

    const newTaData = { data: newRes, hotel: props.hotel };
    setTa?.((prev) => [
      ...prev.filter((it) => it.hotel.htuKey !== newTaData.hotel.htuKey),
      newTaData,
    ]);
  };

  const tabItems = useMemo(() => {
    const items = [
      {
        value: '_g_ico',
        disabled: true,
        label: <img src={gIco} alt="G" className="hotel-google-tab-ico" />,
      },
      { value: '0', label: t('hotel.prices') },
      { value: '1', label: t('hotel.overview') },
      { value: '2', label: t('hotel.reviews') },
      { value: '3', label: t('hotel.photos') },
    ];

    if (!!taData && !!taData.tripAdvisorHotel?.amenities?.length) {
      items.push({
        value: '_t_ico',
        disabled: true,
        label: (
          <span className="ml-a text-bold proposal-tab-label">
            <img src={trIco} alt="T" />
          </span>
        ),
      });
      items.push(
        { value: '10', label: t('hotel.overview') },
        { value: '11', label: t('hotel.reviews') },
        { value: '12', label: 'Amenities' },
      );
    }

    return items;
  }, [taData, t]);

  return (
    <div className="hotel-result-google">
      <div className="">
        <ButtonsLang onClickLoad={onClickLoad} loading={loading} />
        {!!res && !res?.serpResults?.serpHotel && (
          <div className="mt-20 text-bold">Hotel was not found</div>
        )}
        {!!res?.serpResults?.serpHotel && (
          <div className="mt-10">
            <Tabs
              label={t('hotel.overview')}
              className="hotels-tabs w100p mt-5 mb-5"
              size="small"
              value={String(activeTab)}
              onValueChange={(v) => setActiveTab(Number(v))}
              items={tabItems}
            />
            <GoogleTabContent res={res} activeTab={activeTab} />
            <TripadvisorTabContent data={taData} activeTab={activeTab} />
          </div>
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(GoogleHotelInfo, 'GoogleHotelInfo');
